/** How a colleague is named to the person reading - ADR-0218 option C (KD, 2026-11-03, D-15).
 *
 * A record holds a person as an identifier (rule 4). This is the one read that turns identifiers
 * into what the **server** says this reader may be told: a name for the company's own roles, a role
 * for an Auditor, "Erased member" for an erased person, and **no answer** for anyone who is not a
 * member of this workspace. The browser decides nothing about who may see whose name.
 *
 * **Names live in the query cache and nowhere else.** They are never put in a URL, local storage or
 * a log; the cache is keyed by the workspace (`org_id`), so a late answer from the workspace a person
 * has just left cannot appear in the next one, and signing out or switching clears it.
 *
 * **One request for everything on screen.** `labelOf(id)` is called while rendering, once per
 * person per row; each unknown id is queued and one request per tick answers the whole queue, up to
 * the server's bound.
 */

import { useQueryClient } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";
import { useCallback, useSyncExternalStore } from "react";

import { useMe } from "./auth";
import { api } from "./client";
import type { components } from "./schema";

export type PersonLabel = components["schemas"]["PersonLabelOut"];

/** The server's bound on one request (`people_labels.MAX_IDS`). */
const CHUNK = 100;

interface Batcher {
  version: number;
  queue: Set<string>;
  inFlight: Set<string>;
  failed: Set<string>;
  scheduled: boolean;
  listeners: Set<() => void>;
}

const batchers = new WeakMap<QueryClient, Batcher>();

function batcherOf(client: QueryClient): Batcher {
  let found = batchers.get(client);
  if (!found) {
    found = {
      version: 0,
      queue: new Set(),
      inFlight: new Set(),
      failed: new Set(),
      scheduled: false,
      listeners: new Set(),
    };
    batchers.set(client, found);
  }
  return found;
}

const keyOf = (orgId: string, userId: string) => ["person-label", orgId, userId] as const;

/** `null` once asked and not answered (a non-member), a label once answered, `undefined` unknown. */
function cached(client: QueryClient, orgId: string, userId: string): PersonLabel | null | undefined {
  return client.getQueryData<PersonLabel | null>(keyOf(orgId, userId));
}

async function flush(client: QueryClient, orgId: string): Promise<void> {
  const batcher = batcherOf(client);
  batcher.scheduled = false;
  const wanted = [...batcher.queue];
  batcher.queue.clear();
  for (let at = 0; at < wanted.length; at += CHUNK) {
    const ids = wanted.slice(at, at + CHUNK);
    ids.forEach((id) => batcher.inFlight.add(id));
    try {
      const { data } = await api.GET("/api/v1/people/names", { params: { query: { ids } } });
      if (!Array.isArray(data)) throw new Error("people names not readable");
      const answered = new Map(data.map((label) => [label.user_id, label] as const));
      for (const id of ids) client.setQueryData(keyOf(orgId, id), answered.get(id) ?? null);
    } catch {
      // A failed read is not "nobody": the ids stay unknown (the screen uses its own words), and
      // they are remembered as failed for the life of the page so a broken endpoint is not asked
      // again on every render.
      ids.forEach((id) => batcher.failed.add(id));
    } finally {
      ids.forEach((id) => batcher.inFlight.delete(id));
    }
  }
  batcher.version += 1;
  batcher.listeners.forEach((listener) => listener());
}

function want(client: QueryClient, orgId: string, userId: string): void {
  const batcher = batcherOf(client);
  if (
    cached(client, orgId, userId) !== undefined ||
    batcher.queue.has(userId) ||
    batcher.inFlight.has(userId) ||
    batcher.failed.has(userId)
  ) {
    return;
  }
  batcher.queue.add(userId);
  if (!batcher.scheduled) {
    batcher.scheduled = true;
    setTimeout(() => void flush(client, orgId), 0);
  }
}

/** The label the server gave for a person, for the signed-in workspace. `undefined` while it is
 * being read (or when the read failed), `null` when the server does not answer for that id, and
 * never an invented name. The caller chooses the words for the two non-answers. */
export function usePersonLabels(): (userId: string | null | undefined) => PersonLabel | null | undefined {
  const client = useQueryClient();
  const { data: me } = useMe();
  const orgId = me?.org_id;
  const batcher = batcherOf(client);
  const subscribe = useCallback(
    (listener: () => void) => {
      batcher.listeners.add(listener);
      return () => void batcher.listeners.delete(listener);
    },
    [batcher],
  );
  // The snapshot is a version number, so a render re-runs when an answer lands.
  useSyncExternalStore(subscribe, () => batcher.version);
  return (userId) => {
    if (!userId || !orgId) return undefined;
    const label = cached(client, orgId, userId);
    if (label === undefined) want(client, orgId, userId);
    return label;
  };
}
